// we only call this hook from the snapshot toolbar because it scrapes
// HTML from the associated route
import React from 'react'

import { schema } from '@showrunner/prose-schemas'

import {
  parseDocFooters,
  parseDocHeaders,
} from '#choo-app/lib/editor/plugins/configData'
import { showError } from '#components/Modals'
import { useMst } from '#hooks'
import {
  getMergedFormatDefinition,
  getPrintDate,
  getPrintTimestamp,
  // oxlint-disable-next-line no-unused-vars
  saveTextToFile,
} from '#util'
import { DEFAULT_READ_RATE } from '#util/constants'
import { FragmentDiff } from '#util/diffing/FragmentDiff'
import { NodeDiff } from '#util/diffing/NodeDiff'
import { MixpanelEventName } from '#util/mixpanel/eventNames'
import {
  buildPrintableAsteriskHtml,
  buildPrintableDiffHtml,
  buildPrintableScriptHtml,
} from '#util/princePrinting'
import { getBlobAndPrint, getBlobAndSave } from '#util/printing'
import { ScriptSnapshotPayload } from '#util/ScriptoApiClient/types'

import { useDiffAlgorithm } from '../useDiffAlgorithm'
import { useMaybeSnapshot, useScriptData } from '../useSnapshotLandData'

const EXPORT_EVENTS: { [key: string]: { [key: string]: MixpanelEventName } } = {
  download: {
    static: 'SCRIPT_VERSION_EXPORTED_PDF',
    asterisk: 'SCRIPT_REVISION_EXPORTED',
    diff: 'SIDE_BY_SIDE_EXPORTED',
  },
  print: {
    static: 'SCRIPT_VERSION_PRINTED',
    asterisk: 'SCRIPT_REVISION_PRINTED',
    diff: 'SIDE_BY_SIDE_PRINTED',
  },
}

export const useSnapshotExportMenu = ({
  scriptId,
  snap1,
  snap2,
}: {
  scriptId: string
  snap1?: string
  snap2?: string
}) => {
  const [isPrinting, setIsPrinting] = React.useState(false)
  const mst = useMst()
  const scriptQuery = useScriptData(scriptId)
  const snap1Query = useMaybeSnapshot({ scriptId, snapshotId: snap1 })
  const snap2Query = useMaybeSnapshot({ scriptId, snapshotId: snap2 })

  const diff = useDiffAlgorithm()
  const { currentView, trackSnapshotEvent: trackEvent } = mst.view.snapshotLand
  const mstPrefs = mst.user.scriptPrintPrefs
  const { data } = snap1Query
  const scriptName = scriptQuery?.data?.script.name ?? 'Script'

  const canExport =
    currentView === 'static' ? !!data : !!(data && snap2Query.data)

  const handleDownloadFdx = async () => {
    if (!snap1) return

    setIsPrinting(true)
    try {
      await mst.doDebug()
      await mst.currentOrg?.exportSnapshotToFdx({ scriptId, snapshotId: snap1 })
    } catch (e) {
      showError('Failed to create FDX')
    } finally {
      setIsPrinting(false)
    }
  }

  const handleCreatePdf = async (type: 'download' | 'print') => {
    if (!data) return

    const { fileName, html } =
      currentView === 'static'
        ? getStaticParams(data)
        : currentView === 'asterisk'
          ? getAsteriskParams(data)
          : getSideBySideParams()

    setIsPrinting(true)
    const unwatermark = mst.view.shouldUnwatermark

    // DEV HACK: For debugging html generation
    // saveTextToFile({ text: html, fileName: 'asterisk.html' })

    const downloadFn = () =>
      mst.apiClient.passThroughPrint({ html, fileName, unwatermark })

    try {
      await mst.doDebug()
      type === 'print'
        ? await getBlobAndPrint(downloadFn)
        : await getBlobAndSave({ downloadFn, fileName })
      trackEvent(EXPORT_EVENTS[type][currentView])
    } catch (e) {
      const message = 'Failed to ' + (type === 'print' ? 'print' : 'create PDF')
      showError(message)
    } finally {
      setIsPrinting(false)
    }
  }

  function getPrefsFromDoc(doc: ScriptSnapshotPayload['doc']) {
    return {
      ...mstPrefs,
      headers: parseDocHeaders(doc.attrs?.headers),
      footers: parseDocFooters(doc.attrs?.footers),
    }
  }

  function getStaticParams({ doc, scriptFormat }: ScriptSnapshotPayload) {
    const snapshotName = data?.name ?? data?.autoSave ?? 'Snapshot'
    const title = `${scriptName} - ` + snapshotName
    const fileName = title + '.pdf'

    const { definition } = scriptFormat
    const formatDefinition = getMergedFormatDefinition(doc, definition)

    const readRate = mst.currentOrg?.readRate ?? DEFAULT_READ_RATE
    const prefs = getPrefsFromDoc(doc)
    const params = {
      doc,
      formatDefinition,
      prefs,
      readRate,
      title,
    }
    const html = buildPrintableScriptHtml(params)
    return { html, fileName }
  }

  function getAsteriskParams({ doc, scriptFormat }: ScriptSnapshotPayload) {
    const title = scriptName + ' - asterisk'
    const fileName = title + '.pdf'

    const { definition } = scriptFormat
    const formatDefinition = getMergedFormatDefinition(doc, definition)

    const prefs = getPrefsFromDoc(doc)
    const snapshotDoc = snap2Query.data!.doc
    const useRightSideMarks =
      (mst.user.prefs.snapshotRevisionMarkPlacement ?? 'right') === 'right'
    const highlightAdditions = mst.user.prefs.snapshotHighlightAdditions ?? true
    const showDeletions = mst.user.prefs.snapshotShowDeletions ?? false

    const left = schema.nodeFromJSON(snapshotDoc)
    const right = schema.nodeFromJSON(doc)
    const comparison =
      diff.algorithm === 'changeset'
        ? new NodeDiff({ left, right })
        : new FragmentDiff({ left: left.content, right: right.content })

    const readRate = mst.currentOrg?.readRate ?? DEFAULT_READ_RATE
    const params = {
      doc,
      formatDefinition,
      prefs,
      title,
      readRate,
      comparison,
      useRightSideMarks,
      highlightAdditions,
      showDeletions,
    }
    const html = buildPrintableAsteriskHtml(params)
    return { html, fileName }
  }

  function getSideBySideParams() {
    const printableElement = document.querySelector('#side-by-side-wrapper')
    const scrapedHtml = printableElement?.outerHTML ?? ''
    const title = scriptName + ' - side by side'
    const headerHtml =
      printableElement?.querySelector('.sbs-diff_titlerow')?.outerHTML ?? ''
    const fileName = title + '.pdf'

    const html = buildPrintableDiffHtml({
      title,
      html: scrapedHtml,
      headerHtml,
      prefs: {
        ...mstPrefs,
        headers: { enabled: true, showOnFirstPage: true, slots: {} },
      },
      timestamp: getPrintTimestamp(),
      date: getPrintDate(),
    })
    return { html, fileName }
  }

  return { canExport, handleDownloadFdx, handleCreatePdf, isPrinting }
}
